import { zodResolver } from '@hookform/resolvers/zod';
import { type KeyboardEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  type PurchaseOrderRecipientInput,
  type PurchaseOrderRecipientValues,
  purchaseOrderRecipientSchema,
} from '@/features/purchase-order/schema/purchase-order-recipient-schema';

type UsePurchaseOrderSendRecipientsParams = {
  open: boolean;
  recipients: string[];
  onChange: (nextRecipients: string[]) => void;
};

export function usePurchaseOrderSendRecipients({
  open,
  recipients,
  onChange,
}: UsePurchaseOrderSendRecipientsParams) {
  const [showRecipientInput, setShowRecipientInput] = useState(false);

  const form = useForm<
    PurchaseOrderRecipientInput,
    unknown,
    PurchaseOrderRecipientValues
  >({
    defaultValues: { newRecipient: '' },
    resolver: zodResolver(purchaseOrderRecipientSchema),
  });

  useEffect(() => {
    if (open) {
      setShowRecipientInput(false);
    }
  }, [open]);

  const addRecipient = () => {
    const email = form.getValues('newRecipient')?.trim() ?? '';

    if (!email) {
      setShowRecipientInput(false);
      return;
    }

    form.trigger('newRecipient').then((valid) => {
      if (!valid) {
        return;
      }

      const alreadyAdded = recipients.some(
        (recipient) => recipient.toLowerCase() === email.toLowerCase(),
      );
      if (!alreadyAdded) {
        onChange([...recipients, email]);
      }

      setShowRecipientInput(false);
    });
  };

  const handleRecipientKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' && event.key !== ',') {
      return;
    }
    event.preventDefault();
    addRecipient();
  };

  const removeRecipient = (emailToRemove: string) => {
    onChange(recipients.filter((email) => email !== emailToRemove));
  };

  const handleAddClick = () => {
    setShowRecipientInput(true);
  };

  return {
    form,
    showRecipientInput,
    addRecipient,
    handleRecipientKeyDown,
    removeRecipient,
    handleAddClick,
  };
}
